// src/components/veradigm/VeradigmConnectionAlert.jsx

import { useEffect } from 'react';
import { AlertTriangle, CheckCircle } from 'lucide-react';
import { useUser } from '@/contexts/UserContext';
import { apiService } from '@/services/api';
import { useVeradigmStore } from '@/stores/veradigmStore';
import { VeradigmConnectButton } from './VeradigmConnectButton';

export function VeradigmConnectionAlert() {
  const { user } = useUser();
  const { 
    isEnabled, 
    isConnected, 
    isLoading, 
    error, 
    setConnectionStatus, 
    setLoading, 
    setError 
  } = useVeradigmStore();

  
  // Check connection status when component mounts
  useEffect(() => {
    const checkConnection = async () => {
      if (!isEnabled || !user?.id) {
        return;
      }
      setLoading(true);
      try {
        const response = await apiService.checkVeradigmConnection(user.id);
        setConnectionStatus(response.success);
      } catch (err) {
        setError('Error checking Veradigm connection');
        setConnectionStatus(false);
      } finally {
        setLoading(false);
      }
    };
    
    checkConnection();
  }, [user?.id, setConnectionStatus, setLoading, setError]);

  if(!isEnabled || user?.clinical_specialty === 'physical_therapy') {
    return null;
  }
  
  if (isLoading) {
    return (
      <div className="rounded-md bg-blue-50 p-4 mb-6">
        <div className="flex">
          <div className="flex-shrink-0">
            <div className="animate-spin h-5 w-5 border-2 border-blue-500 border-t-transparent rounded-full" />
          </div>
          <div className="ml-3">
            <p className="text-sm text-blue-700">
              Checking Veradigm EHR connection...
            </p>
          </div>
        </div>
      </div>
    );
  }
  
  if (error) {
    return (
      <div className="rounded-md bg-red-50 p-4 mb-6">
        <div className="flex">
          <div className="flex-shrink-0">
            <AlertTriangle className="h-5 w-5 text-red-400" />
          </div>
          <div className="ml-3">
            <p className="text-sm text-red-700">
              {error}
            </p>
          </div>
        </div>
      </div>
    );
  }
  
  if (isConnected) {
    return (
      <div className="rounded-md bg-green-50 p-4 mb-6">
        <div className="flex">
          <div className="flex-shrink-0">
            <CheckCircle className="h-5 w-5 text-green-400" />
          </div>
          <div className="ml-3">
            <p className="text-sm text-green-700">
              Connected to Veradigm EHR
            </p>
          </div>
        </div>
      </div>
    );
  }
  
  // Unconnected state with responsive layout
  return (
    <div className="rounded-md bg-yellow-50 p-4 mb-6">
      <div className="flex flex-col sm:flex-row sm:justify-between sm:items-center">
        <div className="flex mb-3 sm:mb-0">
          <div className="flex-shrink-0">
            <AlertTriangle className="h-5 w-5 text-yellow-400" />
          </div>
          <div className="ml-3">
            <p className="text-sm text-yellow-700">
              Connect to Veradigm EHR to access patient data that's already in your EHR.
            </p>
          </div>
        </div>
        <div className="mt-2 sm:mt-0 flex-shrink-0">
          <VeradigmConnectButton />
        </div>
      </div>
    </div>
  );
}